import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api.service';

interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  priority: string;
  isRead: boolean;
  metadata?: any;
  createdAt: string;
}

const PRIORITY_BADGE: Record<string, string> = {
  LOW: 'bg-gray-100 text-gray-800',
  MEDIUM: 'bg-blue-100 text-blue-800',
  HIGH: 'bg-orange-100 text-orange-800',
  URGENT: 'bg-red-100 text-red-800',
};

export function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<Notification | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getNotifications(1, 50);
      setNotifications(res.data ?? []);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return notifications;
    return notifications.filter(
      (n) =>
        n.title.toLowerCase().includes(q) ||
        n.message.toLowerCase().includes(q) ||
        n.type.toLowerCase().includes(q),
    );
  }, [notifications, searchTerm]);

  const markRead = async (id: string) => {
    try {
      await apiService.markNotificationAsRead(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const markAllRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const unread = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notificaciones</h1>
          <p className="text-sm text-gray-500 mt-1">{unread} sin leer · {total} total</p>
        </div>
        <div className="flex gap-2">
          {unread > 0 && (
            <button onClick={markAllRead} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
              Marcar todas leídas
            </button>
          )}
          <button onClick={load} className="px-4 py-2 text-sm text-blue-600 hover:underline">Actualizar</button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="mb-4">
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar notificaciones…"
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500">No hay notificaciones.</p>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
          {filtered.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => {
                setSelected(n);
                if (!n.isRead) markRead(n.id);
              }}
              className={`w-full text-left px-4 py-4 hover:bg-gray-50 ${!n.isRead ? 'bg-blue-50/40' : ''}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">{n.title}</p>
                  <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {n.type} · {new Date(n.createdAt).toLocaleString('es-AR')}
                  </p>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_BADGE[n.priority] ?? PRIORITY_BADGE.MEDIUM}`}>
                  {n.priority}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold">{selected.title}</h2>
            <p className="text-sm text-gray-600 mt-2">{selected.message}</p>
            <p className="text-xs text-gray-400 mt-4">{new Date(selected.createdAt).toLocaleString('es-AR')}</p>
            <button type="button" onClick={() => setSelected(null)} className="mt-4 text-sm text-blue-600 hover:underline">
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
